import type {
  ActiveActivityRecord,
  Activity,
  ActivityCategory,
  ActivityListResponse,
  ActivitySummary,
  ActivityUpsertPayload,
  ActivityWeeklyTrendEntry,
} from '../types/activities';
import type { ConversationResponse, ConversationMessagesResponse, SendMessagePayload } from '../types/messages';
import { authFetch } from '../lib/authFetch';
import { buildQueryString } from '../lib/query';
import { resolveBase, json } from './client';
import { dedupeRequest } from '../lib/requestDeduplication';
import { createPreview, createShortPreview } from '../lib/text';
import type { ActivityLeaderboardEntry } from '../types/activities';
import type { ApiErrorPayload, PaginationMeta } from './contracts';

const _DEFAULT_PAGE_SIZE = 50;

type ActivityApiResponse = ApiErrorPayload &
  Partial<Activity> & {
    items?: Activity[] | ActiveActivityRecord[] | ActivityLeaderboardEntry[] | ActivityWeeklyTrendEntry[];
    pagination?: PaginationMeta;
    total_events?: number;
    active_records?: number;
    communications?: number;
    follow_up_candidates?: ActivitySummary['follow_up_candidates'];
    top_module?: ActivitySummary['top_module'];
    meta?: ActivitySummary['meta'] | { start: string; end: string; weeks: number };
    conversations?: ConversationResponse['conversations'];
    conversation_id?: string;
    messages?: ConversationMessagesResponse['messages'];
  };

export type ActivityQueryParams = {
  module?: string;
  entityId?: string;
  page?: number;
  page_size?: number;
  activity_type?: string;
  category?: ActivityCategory;
  user_id?: string;
  resolved?: boolean;
  search?: string;
  start?: string;
  end?: string;
  from?: string;
  to?: string;
  include_system?: boolean;
  include_smoke?: boolean;
  timeline_filter?: 'all' | 'operational' | 'emails';
};

async function fetchActivities(apiBase: string, params: ActivityQueryParams = {}): Promise<ActivityListResponse> {
  const base = resolveBase(apiBase);
  const query = buildQueryString(
    {
      module: params.module,
      entity_id: params.entityId,
      page: params.page ? params.page : undefined,
      page_size: params.page_size ? params.page_size : undefined,
      activity_type: params.activity_type || undefined,
      category: params.category || undefined,
      user_id: params.user_id || undefined,
      resolved: params.resolved === undefined ? undefined : params.resolved,
      search: params.search ? params.search : undefined,
      start: params.start || params.from || undefined,
      end: params.end || params.to || undefined,
      include_system: params.include_system ? true : undefined,
      include_smoke: params.include_smoke ? true : undefined,
      timeline_filter: params.timeline_filter || undefined,
    },
    { includeQuestionMark: true },
  );

  const url = `${base}/api/activities${query}`;
  const fetcher = async () => {
    const response = await authFetch(url);
    const data = await json<ActivityApiResponse>(response);
    if (data.status === 'success') {
      return {
        status: 'success',
        items: data.items || [],
        pagination: data.pagination || {
          page: 1,
          page_size: _DEFAULT_PAGE_SIZE,
          total: 0,
          total_pages: 1,
        },
      } as ActivityListResponse;
    }
    return data as ActivityListResponse;
  };

  return dedupeRequest(url, fetcher);
}

export async function listActivities(
  apiBase: string,
  module: string,
  entityId: string,
  params: {
    page?: number;
    page_size?: number;
    activity_type?: string;
    user_id?: string;
    resolved?: boolean;
    search?: string;
    timeline_filter?: 'all' | 'operational' | 'emails';
  } = {}
): Promise<ActivityListResponse> {
  const base = resolveBase(apiBase);
  const query = buildQueryString(
    {
      module,
      entity_id: entityId,
      page: params.page ? params.page : undefined,
      page_size: params.page_size ? params.page_size : undefined,
      activity_type: params.activity_type || undefined,
      user_id: params.user_id || undefined,
      resolved: params.resolved === undefined ? undefined : params.resolved,
      search: params.search ? params.search : undefined,
      timeline_filter: params.timeline_filter || undefined,
    },
    { includeQuestionMark: true },
  );

  const url = `${base}/api/activities${query}`;
  const fetchActivities = async () => {
    const response = await authFetch(url);
    const data = await json<ActivityApiResponse>(response);
    if (data.status === 'success') {
      return {
        status: 'success',
        items: data.items || [],
        pagination: data.pagination || {
          page: 1,
          page_size: _DEFAULT_PAGE_SIZE,
          total: 0,
          total_pages: 1,
        },
      } as ActivityListResponse;
    }
    return data as ActivityListResponse;
  };

  return dedupeRequest(url, fetchActivities);
}

export async function listAllActivities(apiBase: string, params: ActivityQueryParams = {}): Promise<ActivityListResponse> {
  return fetchActivities(apiBase, params);
}

export async function listActiveActivityRecords(
  apiBase: string,
  params: ActivityQueryParams & { limit?: number } = {},
): Promise<ActiveActivityRecord[]> {
  const base = resolveBase(apiBase);
  const query = buildQueryString(
    {
      module: params.module,
      activity_type: params.activity_type || undefined,
      category: params.category || undefined,
      user_id: params.user_id || undefined,
      search: params.search ? params.search : undefined,
      start: params.start || params.from || undefined,
      end: params.end || params.to || undefined,
      include_system: params.include_system ? true : undefined,
      include_smoke: params.include_smoke ? true : undefined,
      limit: params.limit || undefined,
    },
    { includeQuestionMark: true },
  );
  const url = `${base}/api/activities/active-records${query}`;
  const response = await authFetch(url);
  const data = await json<ActivityApiResponse>(response);
  if (data.status === 'success') {
    return (data.items || []) as ActiveActivityRecord[];
  }
  throw new Error(data.message || 'Failed to load active records');
}

export async function getActivitySummary(
  apiBase: string,
  params: ActivityQueryParams = {},
): Promise<ActivitySummary> {
  const base = resolveBase(apiBase);
  const query = buildQueryString(
    {
      module: params.module,
      activity_type: params.activity_type || undefined,
      category: params.category || undefined,
      user_id: params.user_id || undefined,
      search: params.search ? params.search : undefined,
      start: params.start || params.from || undefined,
      end: params.end || params.to || undefined,
      include_system: params.include_system ? true : undefined,
      include_smoke: params.include_smoke ? true : undefined,
    },
    { includeQuestionMark: true },
  );
  const url = `${base}/api/activities/summary${query}`;
  const response = await authFetch(url);
  const data = await json<ActivityApiResponse>(response);
  if (data.status === 'success') {
    return {
      total_events: data.total_events ?? 0,
      active_records: data.active_records ?? 0,
      communications: data.communications ?? 0,
      follow_up_candidates: data.follow_up_candidates || [],
      top_module: data.top_module || null,
      meta: data.meta,
    } as ActivitySummary;
  }
  throw new Error(data.message || 'Failed to load activity summary');
}

export async function listActivityLeaderboard(
  apiBase: string,
  params: { range?: 'today' | 'this_week' | 'last_week' | 'this_month'; include_system?: boolean } = {},
): Promise<ActivityLeaderboardEntry[]> {
  const base = resolveBase(apiBase);
  const query = buildQueryString(
    {
      range: params.range || 'this_week',
      include_system: params.include_system ? true : undefined,
    },
    { includeQuestionMark: true },
  );
  const url = `${base}/api/activities/leaderboard${query}`;
  const response = await authFetch(url);
  const data = await json<ActivityApiResponse>(response);
  if (data.status === 'success') {
    return (data.items || []) as ActivityLeaderboardEntry[];
  }
  throw new Error(data.message || 'Failed to load leaderboard');
}

export async function listActivityWeeklyTrend(
  apiBase: string,
  params: { weeks?: number; include_system?: boolean } = {},
): Promise<{ items: ActivityWeeklyTrendEntry[]; meta: { start: string; end: string; weeks: number } }> {
  const base = resolveBase(apiBase);
  const query = buildQueryString(
    {
      weeks: params.weeks || 12,
      include_system: params.include_system ? true : undefined,
    },
    { includeQuestionMark: true },
  );
  const url = `${base}/api/activities/weekly-trend${query}`;
  const response = await authFetch(url);
  const data = await json<ActivityApiResponse>(response);
  if (data.status === 'success') {
    const meta = data.meta && 'weeks' in data.meta
      ? data.meta
      : { start: '', end: '', weeks: 12 };
    return {
      items: (data.items || []) as ActivityWeeklyTrendEntry[],
      meta,
    };
  }
  throw new Error(data.message || 'Failed to load weekly trend');
}

export async function getActivityById(apiBase: string, activityId: string): Promise<Activity> {
  const base = resolveBase(apiBase);
  const response = await authFetch(`${base}/api/activities/${activityId}`);
  const data = await json<ActivityApiResponse>(response);
  
  if (data.status === 'success') {
    const { status, ...activity } = data;
    return activity as Activity;
  }
  return data as Activity;
}

export async function createActivity(
  apiBase: string,
  payload: ActivityUpsertPayload
): Promise<Activity> {
  const base = resolveBase(apiBase);
  const response = await authFetch(`${base}/api/activities`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const data = await json<ActivityApiResponse>(response);
  
  if (data.status === 'success') {
    const { status, ...activity } = data;
    return activity as Activity;
  }
  throw new Error(data.message || 'Failed to create activity');
}

export async function updateActivity(
  apiBase: string,
  activityId: string,
  payload: Partial<ActivityUpsertPayload & { resolved?: boolean }>
): Promise<Activity> {
  const base = resolveBase(apiBase);
  const response = await authFetch(`${base}/api/activities/${activityId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  const data = await json<ActivityApiResponse>(response);
  
  if (data.status === 'success') {
    const { status, ...activity } = data;
    return activity as Activity;
  }
  throw new Error(data.message || 'Failed to update activity');
}

export async function logCommunication(
  apiBase: string,
  module: string,
  entityId: string,
  communicationType: 'phone' | 'email' | 'meeting' | 'text',
  notes: string,
  entityName?: string,
  title?: string
): Promise<Activity> {
  const trimmedNotes = notes.trim();
  const trimmedTitle = title?.trim() ?? '';
  const message = trimmedTitle || `${communicationType} logged`;
  const preview = trimmedNotes ? createPreview(trimmedNotes, 100) : undefined;

  return createActivity(apiBase, {
    related_to: {
      module,
      entity_id: entityId,
      entity_name: entityName,
    },
    activity_type: 'communication',
    message,
    text: trimmedNotes,
    details: {
      communication_type: communicationType,
      note_preview: preview,
      note_title: trimmedTitle || undefined,
    },
  });
}

export async function createComment(
  apiBase: string,
  module: string,
  entityId: string,
  text: string,
  commentType: string = 'general',
  entityName?: string,
  attachments?: string[]
): Promise<Activity> {
  return createActivity(apiBase, {
    related_to: {
      module,
      entity_id: entityId,
      entity_name: entityName,
    },
    activity_type: 'comment',
    message: `Comment added: ${createShortPreview(text)}`,
    text,
    details: {
      comment_type: commentType,
    },
    attachments: attachments || [],
    resolved: false,
  });
}

export async function sendMessage(
  apiBase: string,
  recipients: string[],
  text: string,
  conversationId?: string
): Promise<Activity> {
  const base = resolveBase(apiBase);
  const payload: SendMessagePayload = {
    recipients,
    text,
  };
  if (conversationId) {
    payload.conversation_id = conversationId;
  }
  
  const response = await authFetch(`${base}/api/activities/message`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const data = await json<ActivityApiResponse>(response);
  
  if (data.status === 'success') {
    const { status, ...activity } = data;
    return activity as Activity;
  }
  throw new Error(data.message || 'Failed to send message');
}

export async function getConversations(apiBase: string): Promise<ConversationResponse> {
  const base = resolveBase(apiBase);
  const response = await authFetch(`${base}/api/activities/conversations`, {
    headers: {
      'X-Skip-Universal-Log': 'true',
    },
  });
  const data = await json<ActivityApiResponse>(response);
  
  if (data.status === 'success') {
    return {
      conversations: data.conversations || [],
    };
  }
  throw new Error(data.message || 'Failed to get conversations');
}

export async function getConversationMessages(
  apiBase: string,
  conversationId: string,
  options: { signal?: AbortSignal } = {},
): Promise<ConversationMessagesResponse> {
  const base = resolveBase(apiBase);
  const response = await authFetch(`${base}/api/activities/conversations/${conversationId}`, {
    signal: options.signal,
    headers: {
      'X-Skip-Universal-Log': 'true',
    },
  });
  const data = await json<ActivityApiResponse>(response);
  
  if (data.status === 'success') {
    return {
      conversation_id: data.conversation_id as string,
      messages: data.messages || [],
    };
  }
  throw new Error(data.message || 'Failed to get conversation messages');
}

export async function editMessage(
  apiBase: string,
  activityId: string,
  text: string,
  message?: string
): Promise<Activity> {
  const base = resolveBase(apiBase);
  const response = await authFetch(`${base}/api/activities/${activityId}`, {
    method: 'PATCH',
    body: JSON.stringify({ text, message: message || createShortPreview(text) }),
  });
  const data = await json<ActivityApiResponse>(response);
  
  if (data.status === 'success') {
    const { status, ...activity } = data;
    return activity as Activity;
  }
  throw new Error(data.message || 'Failed to edit message');
}

export async function deleteMessage(apiBase: string, activityId: string): Promise<void> {
  const base = resolveBase(apiBase);
  const response = await authFetch(`${base}/api/activities/${activityId}`, {
    method: 'DELETE',
  });
  const data = await json<ActivityApiResponse>(response);
  
  if (data.status !== 'success') {
    throw new Error(data.message || 'Failed to delete message');
  }
}

export async function addReaction(
  apiBase: string,
  activityId: string,
  reaction: 'thumbs_up' | 'thumbs_down' | 'question' | 'laugh'
): Promise<Activity> {
  const base = resolveBase(apiBase);
  const response = await authFetch(`${base}/api/activities/${activityId}`, {
    method: 'PATCH',
    body: JSON.stringify({ reaction, action: 'add' }),
  });
  const data = await json<ActivityApiResponse>(response);
  
  if (data.status === 'success') {
    const { status, ...activity } = data;
    return activity as Activity;
  }
  throw new Error(data.message || 'Failed to add reaction');
}

export async function removeReaction(
  apiBase: string,
  activityId: string,
  reaction: 'thumbs_up' | 'thumbs_down' | 'question' | 'laugh'
): Promise<Activity> {
  const base = resolveBase(apiBase);
  const response = await authFetch(`${base}/api/activities/${activityId}`, {
    method: 'PATCH',
    body: JSON.stringify({ reaction, action: 'remove' }),
  });
  const data = await json<ActivityApiResponse>(response);
  
  if (data.status === 'success') {
    const { status, ...activity } = data;
    return activity as Activity;
  }
  throw new Error(data.message || 'Failed to remove reaction');
}
